import React from 'react'
import { styled } from '@mui/material/styles'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'packages/eid-ui'
import { VerticalSquare } from 'images'

export const SwitchContainer = styled(({ assignedToMe, ...rest }) => (
    <Box {...rest} />
))(({ assignedToMe }) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: '4px',
    '& >p': {
        fontSize: '14px',
    },
    '& .Label': {
        color: assignedToMe ? '#8b909a' : '#000000',
    },
    '& .SubLabel': {
        color: assignedToMe ? '#000000' : '#8b909a',
    },
    '& .MuiFormControlLabel-root': {
        marginLeft: '10px',
    },
}))

const maxCharacters = 80

const tooltipStyles = {
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    padding: '8px',
    maxWidth: '500px',
    backgroundColor: '#0057A0',
}

const textStyles = {
    display: 'block',
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    maxHeight: '200px',
    minWidth: '12rem',
    overflow: 'auto',
}

export const TextFormatter = ({ attribute, item }) => {
    const { t } = useTranslation()

    var getValue = (ci, attribute) => {
        let value = ci[attribute.name]
        if (typeof value === 'boolean') {
            return (
                <>
                    {typeof value === 'boolean' && value === true ? (
                        <>{t('Yes')}</>
                    ) : (
                        <>{t('No')}</>
                    )}
                </>
            )
        } else {
            return ci[attribute.name] ?? ''
        }
    }

    let wrappedCellContent

    const cellContent = (
        <Box style={textStyles}>
            {getValue(item, attribute).length > maxCharacters
                ? `${getValue(item, attribute).substring(0, maxCharacters)}...`
                : getValue(item, attribute)}
        </Box>
    )

    wrappedCellContent =
        getValue(item, attribute).length > maxCharacters ? (
            <Tooltip
                title={getValue(item, attribute)}
                color="#5D6870"
                fontColor="#ffffff"
                enterDelay={500}
                enterNextDelay={500}
                disableInteractive={false}
                tooltipStyles={tooltipStyles}
                placement="bottom-start"
                arrow={false}
            >
                {cellContent}
            </Tooltip>
        ) : (
            cellContent
        )

    return <>{wrappedCellContent}</>
}

export const colors = {
    primary: '#0057A0',
    grey: '#7d7c7c',
}

export const styles = {
    tableHeaderContent: {
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: 'inherit',
        justifyContent: 'flex-start',
    },
    green: {
        color: '#4caf50',
    },
    black: {
        color: '#424242',
    },
    blue: {
        color: colors.primary,
    },
    boldBlue: {
        color: colors.primary,
        fontWeight: 'bold',
    },
    underline: {
        textDecoration: 'underline',
    },
    greyColor: {
        color: 'grey !important',
    },
    table: {
        backgroundColor: '#ffffff',
        '& tr:hover': {
            backgroundColor: '#f7f8fa',
        },
    },
    tableRow: {
        border: ' solid 1px #efeff1',
    },
    highlightedRow: {
        border: ' solid 1px  #0057A0',
        boxShadow: '4px 4px 15px 0 rgba(48, 127, 193, 0.15)',
    },

    tableCell: {
        padding: '15px 8px',
        fontSize: '13px',
        borderBottom: '0',
        '&:first-of-type': {
            padding: '15px 8px',
        },
    },
    tableCellHighlighted: {
        borderTop: ' solid 1px #0057A0',
        padding: '15px 8x',
        fontSize: '13px',
        borderBottom: '0',
        '&:first-of-type': {
            padding: '15px 8px',
        },
    },
    rowCheckbox: {
        '& input[type="checkbox"]:focus-visible + svg rect': {
            stroke: '#0057A0',
            strokeWidth: '2',
        },
    },
    hilightFont: {
        fontSize: '15px',
    },
    rowSelect: {
        borderBottom: '1px solid grey',
        borderRadius: '0',
    },
    tablePagination: {
        color: 'grey !important',
    },
    bigAvatar: {
        marginLeft: '4px',
        width: 50,
        height: 50,
    },
    loadButton: {
        color: colors.primary,
        width: '100%',
        textTransform: 'inherit',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    iconRegular: {
        width: '20px',
        height: '20px',
        objectFit: 'contain',
    },
    iconSpacing: {
        marginRight: '7px',
    },
}

export const renderIcon = (heading, sort, sortBy, sortOrder) => {
    return (
        heading.icons && (
            <span style={styles.blue}>
                {heading.icons.map((i, index) => (
                    <img
                        style={{
                            ...styles.iconRegular,
                            ...styles.iconSpacing,
                        }}
                        key={`icon-image-${heading.name}-${index}`}
                        src={`${config.APP_SUBPATH}${i}`}
                        alt={''}
                    />
                ))}
            </span>
        )
    )
}

export const config = {
    businessRequestDelegations: {
        dataSource: {
            type: 'MS_DS',
            url: '/api/v1/Delegation/Delegator',
            deleteUrl: '/api/v1/Delegation/Delete',
        },
        subHeaderButton: {
            name: 'CreateNewBusinessRequestDelegateButton',
            requireAccess: {
                control:
                    'MyIdentity-PermanentDelegationsCreatePermanentDelegation-Control',
            },
        },
        filters: [
            {
                dynamic: true,
                name: 'AdvancedSearch',
                title: 'AdvancedSearch',
                icon: {
                    name: 'AdvancedSearch',
                    color: '#959598',
                },
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsAdvancedSearch-Control',
                },

                type: 'advancedSearch',
                forms: {
                    colorCode: {
                        primary: '#2920ad',
                        secondary: '#ebeaf7',
                    },
                    filters: [
                        {
                            name: 'personName',
                            type: 'autocomplete',
                            label: 'PersonName',
                            placeholder: 'PersonName',
                            optionConfig: {
                                type: 'person',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/approvers',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            contextProp: 'advancedSearch.forms.delegatee',
                            queryParam: {
                                name: 'PersonNameId',
                                valueProp: 'id',
                            },
                        },
                        {
                            name: 'approvalStep',
                            type: 'autocomplete',
                            label: 'ApprovalStep',
                            supportsServerSideSearch: false,
                            placeholder: 'ApprovalStep',
                            contextProp: 'advancedSearch.forms.delegationType',
                            optionConfig: {
                                type: 'label',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/types',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'startDate',
                            type: 'startDate',
                            label: 'StartDate',
                            title: 'StartDate',
                            placeholder: 'StartDate',
                            contextProp: 'advancedSearch.forms.startDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'endDate',
                            type: 'endDate',
                            label: 'EndDate',
                            title: 'EndDate',
                            placeholder: 'EndDate',
                            contextProp: 'advancedSearch.forms.endDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                    ],
                },
            },
        ],

        attributes: [
            {
                name: 'delegateePathToImage',
                hideValue: true,
                avatar: true,
                src: 'delegateePathToImage',
                label: '',
                align: 'center',
                sort: false,
                styles: {
                    width: '70px',
                },
            },
            {
                name: 'delegateeFriendlyName',
                label: 'PersonName',
                icon: VerticalSquare,
                align: 'left',
                sort: true,
                asc: true,
                styles: {
                    fontSize: '15px',
                    color: '#0057A0',
                },
            },

            {
                name: 'approvalFlowStepFriendlyName',
                label: 'ApprovalStep',
                icon: 'normal',
                align: 'left',
                sort: true,
                asc: true,
            },
            {
                name: 'startDateUtc',
                columnName: 'startDate',
                label: 'StartDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
                styles: { minWidth: '80px' },
            },
            {
                name: 'endDateUtc',
                columnName: 'endDate',
                label: 'EndDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
                styles: { minWidth: '80px' },
            },
            {
                name: 'activeOnlyIfOutOfOffice',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
                },
                label: 'OnlyWhenOutOfOffice',
                align: 'left',
                sort: false,
                styles: { maxWidth: '100px' },
            },
            {
                name: 'justificationComment',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegations-JustificationColumn-Control',
                },
                label: 'Justification',
                align: 'left',
                sort: true,
                hideValue: true,
                component: {
                    name: 'CellJustification',
                },
            },
            {
                name: 'editButton',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsEditPermanentDelegation-Control',
                },
                label: '',
                icon: '',
                align: 'center',
                sort: false,
                hideValue: true,
                disabledFlagKey: 'isDelegateePersonActive',
                component: {
                    name: 'EditDelegateButton',
                    editUrl: '/api/v1/Delegation/Edit',
                    revalidateUrl: '/api/v1/Delegation/Delegator',
                },
                styles: { width: '60px' },
            },
            {
                name: 'deleteButton',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsDeletePermanentDelegation-Control',
                },
                label: '',
                icon: '',
                align: 'center',
                sort: false,
                hideValue: true,
                component: {
                    name: 'DeleteDelegateButton',
                    revalidateUrl: '/api/v1/Delegation/Delegator',
                    deleteUrl: '/api/v1/Delegation/Delete',
                    primaryKey: 'id',
                },
                styles: { width: '80px', paddingRight: '20px' },
            },
        ],
    },

    businessRequestAssignments: {
        dataSource: {
            type: 'MS_DS',
            url: '/api/v1/Delegation/Delegatee',
            deleteUrl: '/api/v1/Delegation/Delete',
        },
        filters: [
            {
                dynamic: true,
                name: 'AdvancedSearch',
                title: 'AdvancedSearch',
                icon: {
                    name: 'AdvancedSearch',
                    color: '#959598',
                },
                requireAccess: {
                    control:
                        'MyIdentity-PermanentAssignmentsAdvancedSearch-Control',
                },

                type: 'advancedSearch',
                forms: {
                    colorCode: {
                        primary: '#2920ad',
                        secondary: '#ebeaf7',
                    },
                    filters: [
                        {
                            name: 'personName',
                            type: 'autocomplete',
                            label: 'PersonName',
                            placeholder: 'PersonName',
                            optionConfig: {
                                type: 'person',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/approvers',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            contextProp: 'advancedSearch.forms.delegator',
                            queryParam: {
                                name: 'PersonNameId',
                                valueProp: 'id',
                            },
                        },
                        {
                            name: 'approvalStep',
                            type: 'autocomplete',
                            label: 'ApprovalStep',
                            supportsServerSideSearch: false,
                            placeholder: 'ApprovalStep',
                            contextProp: 'advancedSearch.forms.delegationType',
                            optionConfig: {
                                type: 'label',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/types',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'startDate',
                            type: 'startDate',
                            label: 'StartDate',
                            title: 'StartDate',
                            placeholder: 'StartDate',
                            contextProp: 'advancedSearch.forms.startDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'endDate',
                            type: 'endDate',
                            label: 'EndDate',
                            title: 'EndDate',
                            placeholder: 'EndDate',
                            contextProp: 'advancedSearch.forms.endDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                    ],
                },
            },
        ],

        attributes: [
            {
                name: 'delegatorPathToImage',
                hideValue: true,
                avatar: true,
                src: 'delegatorPathToImage',
                label: '',
                align: 'center',
                sort: false,
                styles: {
                    width: '100px',
                },
            },
            {
                name: 'delegatorFriendlyName',
                label: 'PersonName',
                icon: VerticalSquare,
                align: 'left',
                sort: true,
                asc: true,
                styles: { fontSize: '15px', color: '#0057A0' },
            },

            {
                name: 'approvalFlowStepFriendlyName',
                label: 'ApprovalStep',
                icon: 'normal',
                align: 'left',
                sort: true,
                asc: true,
            },
            {
                name: 'startDateUtc',
                columnName: 'startDate',
                label: 'StartDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
            },
            {
                name: 'endDateUtc',
                columnName: 'endDate',
                label: 'EndDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
            },
            {
                name: 'activeOnlyIfOutOfOffice',
                label: 'OnlyWhenOutOfOffice',
                align: 'left',
                sort: false,
            },
            {
                name: 'justificationComment',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegations-JustificationColumn-Control',
                },
                label: 'Justification',
                align: 'left',
                sort: true,
                hideValue: true,
                component: {
                    name: 'CellJustification',
                },
            },
        ],
    },
    collaborationTasksDelegations: {
        dataSource: {
            type: 'MS_DS',
            url: '/api/v1/Delegation/WorkflowTaskDelegations/Delegator',
            deleteUrl: '/api/v1/Delegation/WorkflowTaskDelegations/Delete',
        },
        subHeaderButton: {
            name: 'CreateNewCollaborationTasksDelegateButton',
            requireAccess: {
                control:
                    'MyIdentity-PermanentDelegationsCreateCollaborationTaskDelegation-Control',
            },
        },
        filters: [
            {
                dynamic: true,
                name: 'AdvancedSearch',
                title: 'AdvancedSearch',
                icon: {
                    name: 'AdvancedSearch',
                    color: '#959598',
                },
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsAdvancedSearch-Control',
                },

                type: 'advancedSearch',
                forms: {
                    colorCode: {
                        primary: '#2920ad',
                        secondary: '#ebeaf7',
                    },
                    filters: [
                        {
                            name: 'personName',
                            type: 'autocomplete',
                            label: 'PersonName',
                            placeholder: 'PersonName',
                            optionConfig: {
                                type: 'person',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/approvers',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            contextProp: 'advancedSearch.forms.delegatee',
                            queryParam: {
                                name: 'PersonNameId',
                                valueProp: 'id',
                            },
                        },
                        {
                            name: 'approvalStep',
                            type: 'autocomplete',
                            label: 'ApprovalStep',
                            supportsServerSideSearch: false,
                            placeholder: 'ApprovalStep',
                            contextProp: 'advancedSearch.forms.delegationType',
                            optionConfig: {
                                type: 'label',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/types',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'startDate',
                            type: 'startDate',
                            label: 'StartDate',
                            title: 'StartDate',
                            placeholder: 'StartDate',
                            contextProp: 'advancedSearch.forms.startDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'endDate',
                            type: 'endDate',
                            label: 'EndDate',
                            title: 'EndDate',
                            placeholder: 'EndDate',
                            contextProp: 'advancedSearch.forms.endDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                    ],
                },
            },
        ],

        attributes: [
            {
                name: 'delegateePathToImage',
                hideValue: true,
                avatar: true,
                src: 'delegateePathToImage',
                label: '',
                align: 'center',
                sort: false,
                styles: {
                    width: '70px',
                },
            },
            {
                name: 'delegateeFriendlyName',
                label: 'PersonName',
                icon: VerticalSquare,
                align: 'left',
                sort: true,
                asc: true,
                styles: {
                    fontSize: '15px',
                    color: '#0057A0',
                },
            },
            {
                name: 'startDateUtc',
                columnName: 'startDate',
                label: 'StartDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
                styles: { minWidth: '80px' },
            },
            {
                name: 'endDateUtc',
                columnName: 'endDate',
                label: 'EndDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
                styles: { minWidth: '80px' },
            },
            {
                name: 'activeOnlyIfOutOfOffice',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
                },
                label: 'OnlyWhenOutOfOffice',
                align: 'left',
                sort: false,
                styles: { maxWidth: '100px' },
            },
            {
                name: 'justificationComment',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegations-JustificationColumn-Control',
                },
                label: 'Justification',
                align: 'left',
                sort: false,
                hideValue: true,
                component: {
                    name: 'CellJustification',
                },
                styles: { minWidth: '150px', maxWidth: '250px' },
            },
            {
                name: 'editButton',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsEditPermanentDelegation-Control',
                },
                label: '',
                icon: '',
                align: 'center',
                sort: false,
                hideValue: true,
                component: {
                    name: 'EditDelegateButton',
                    editUrl: '/api/v1/Delegation/workflowTaskDelegation/update',
                    revalidateUrl:
                        '/api/v1/Delegation/WorkflowTaskDelegations/Delegator',
                    appendData: true,
                },
                styles: { width: '60px' },
            },
            {
                name: 'deleteButton',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegationsDeletePermanentDelegation-Control',
                },
                label: '',
                icon: '',
                align: 'center',
                sort: false,
                hideValue: true,
                component: {
                    name: 'DeleteDelegateButton',
                    revalidateUrl:
                        '/api/v1/Delegation/WorkflowTaskDelegations/Delegator',
                    deleteUrl:
                        '/api/v1/Delegation/WorkflowTaskDelegations/Delete',
                    primaryKey: 'delegateePersonId',
                    appendDelegatorId: false,
                },
                styles: { width: '80px', paddingRight: '20px' },
            },
        ],
    },

    collaborationTasksAssignments: {
        dataSource: {
            type: 'MS_DS',
            url: '/api/v1/Delegation/WorkflowTaskDelegations/Delegatee',
        },
        filters: [
            {
                dynamic: true,
                name: 'AdvancedSearch',
                title: 'AdvancedSearch',
                icon: {
                    name: 'AdvancedSearch',
                    color: '#959598',
                },
                requireAccess: {
                    control:
                        'MyIdentity-PermanentAssignmentsAdvancedSearch-Control',
                },

                type: 'advancedSearch',
                forms: {
                    colorCode: {
                        primary: '#2920ad',
                        secondary: '#ebeaf7',
                    },
                    filters: [
                        {
                            name: 'personName',
                            type: 'autocomplete',
                            label: 'PersonName',
                            placeholder: 'PersonName',
                            optionConfig: {
                                type: 'person',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/approvers',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            contextProp: 'advancedSearch.forms.delegator',
                            queryParam: {
                                name: 'PersonNameId',
                                valueProp: 'id',
                            },
                        },
                        {
                            name: 'approvalStep',
                            type: 'autocomplete',
                            label: 'ApprovalStep',
                            supportsServerSideSearch: false,
                            placeholder: 'ApprovalStep',
                            contextProp: 'advancedSearch.forms.delegationType',
                            optionConfig: {
                                type: 'label',
                                labelProp: 'friendlyName',
                                imageUrlProp: 'imageUrl',
                            },
                            url: '/api/v1/Delegation/types',
                            queryParams: {
                                take: 10,
                            },
                            debounceInterval: 500,
                            dedupingInterval: 60000,
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'startDate',
                            type: 'startDate',
                            label: 'StartDate',
                            title: 'StartDate',
                            placeholder: 'StartDate',
                            contextProp: 'advancedSearch.forms.startDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                        {
                            name: 'endDate',
                            type: 'endDate',
                            label: 'EndDate',
                            title: 'EndDate',
                            placeholder: 'EndDate',
                            contextProp: 'advancedSearch.forms.endDate',
                            queryParam: {
                                name: 'Name',
                                type: 'string',
                            },
                        },
                    ],
                },
            },
        ],

        attributes: [
            {
                name: 'delegatorPathToImage',
                hideValue: true,
                avatar: true,
                src: 'delegatorPathToImage',
                label: '',
                align: 'center',
                sort: false,
                styles: {
                    width: '100px',
                },
            },
            {
                name: 'delegatorFriendlyName',
                label: 'PersonName',
                icon: VerticalSquare,
                align: 'left',
                sort: true,
                asc: true,
                styles: { fontSize: '15px', color: '#0057A0' },
            },
            {
                name: 'startDateUtc',
                columnName: 'startDate',
                label: 'StartDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
            },
            {
                name: 'endDateUtc',
                columnName: 'endDate',
                label: 'EndDate',
                icon: VerticalSquare,
                align: 'left',
                hideValue: true,
                component: {
                    name: 'CellDateTime',
                },
                sort: true,
                asc: true,
            },
            {
                name: 'activeOnlyIfOutOfOffice',
                label: 'OnlyWhenOutOfOffice',
                align: 'left',
                sort: false,
            },
            {
                name: 'justificationComment',
                requireAccess: {
                    control:
                        'MyIdentity-PermanentDelegations-JustificationColumn-Control',
                },
                label: 'Justification',
                align: 'left',
                sort: false,
                hideValue: true,
                component: {
                    name: 'CellJustification',
                },
                styles: { minWidth: '150px', maxWidth: '250px' },
            },
        ],
    },
}
